/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Action,
  ActionIcon,
  Card,
  CardProps,
  CardSkeleton,
  Dropdown,
  FontWeight, FormHandlers, HStack, ICON_PLUS, ICON_SYNC, Message, ModalSize, Portal, Spacing, Stack, Text, TextSkeleton, useModal,
} from '@doveit/bricks';
import { uniqueId } from 'lodash';
import { pluralizeWord } from '@doveit/hammer';
import { AgentReview } from '../../../providers/api/dtos';
import { useSlotSelector } from '../../../hooks/use-slot-selector/useSlotSelector';
import useReviewsByAgentEmail from '../../../review/hooks/use-reviews-by-agent-email/useReviewsByAgentEmail';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import ScrollableArea from '../../../components/scrollable-area/ScrollableArea';
import ReviewAvatar from '../../../review/components/ReviewAvatar/ReviewAvatar';
import AgentReviewSelectablePreview from '../../components/agent-review-selectable-preview/AgentReviewSelectablePreview';
import * as styles from './AgentReviewsSelector.style';
import SearchByQueryAction from '../../../components/search-by-query-action/SearchByQueryAction';
import type { BrandTemplateAutofillDecodedDataset } from '../../../marketing/utils/autofill/types';
import TemplateFieldsForm from '../../../marketing/components/template-fields-form/TemplateFieldsForm';
import { AGENT_DATA_PREVIEWS_TEMPLATE_FIELD_CONFIG } from '../../../marketing/pages/use-brand-template/config/brandTemplateFieldConfig';
import { formatInputDate } from '../../../utils/form';
import { TemplateFieldsFormValues } from '../../../marketing/types';

export type AgentReviewsSelectorSlotsConfig = Map<number, AgentReview>;

export interface AgentReviewsSelectorProps {
  agentEmail: string,
  totalSlots: number,
  disabled?: boolean,
  decodedDataset: BrandTemplateAutofillDecodedDataset,
  onChange?: (slots: AgentReviewsSelectorSlotsConfig) => void,
  asContent?: CardProps['asContent'],
}

type ModalData = {
  slotIndex: number,
  review?: AgentReview,
};

const AgentReviewsSelector: React.FC<AgentReviewsSelectorProps> = ({
  agentEmail,
  totalSlots,
  decodedDataset,
  disabled = false,
  asContent,
  onChange,
}) => {
  const isMobile = useIsDevice('mobile');
  const modal = useModal<ModalData>();
  const editModal = useModal<ModalData>();

  const editReviewFormRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const [searchQuery, setSearchQuery] = React.useState<string>();

  const {
    data: preSelectedReviews,
    isLoading: arePreSelectedReviewsLoading,
    error: preSelectedReviewsError,
  } = useReviewsByAgentEmail({ agentEmail }, { pageSize: totalSlots });

  const {
    data: allReviews,
    isLoading: areAllReviewsLoading,
    error: allReviewsError,
  } = useReviewsByAgentEmail((modal.data && agentEmail) ? {
    agentEmail,
    query: searchQuery,
  } : undefined);

  const [reviewsSlots, setReviewSlots] = React.useState<AgentReviewsSelectorSlotsConfig>(new Map());

  const { assignSlot, deselectSlot } = useSlotSelector<AgentReview>({
    totalSlots,
    initialItems: preSelectedReviews,
    resetKey: agentEmail,
    onChange: setReviewSlots,
  });

  React.useEffect(() => {
    onChange?.(reviewsSlots);
  }, [onChange, reviewsSlots]);

  const selectedReviewIds = React.useMemo(() => new Set([...reviewsSlots.values()].map((c) => c.id!)), [reviewsSlots]);

  const slotIndexByReviewId = React.useMemo(() => {
    const map = new Map<string, number>();
    reviewsSlots.forEach((review, slotIndex) => {
      if (review.id) map.set(review.id, slotIndex);
    });
    return map;
  }, [reviewsSlots]);

  const openModal = React.useCallback((slotIndex: number) => () => {
    if (disabled) return;

    setSearchQuery(undefined);

    modal.open({ slotIndex, review: reviewsSlots.get(slotIndex) });
  }, [modal, reviewsSlots, disabled]);

  const openEditModal = React.useCallback((slotIndex: number) => () => {
    if (disabled) return;

    setSearchQuery(undefined);

    editModal.open({ slotIndex, review: reviewsSlots.get(slotIndex) });
  }, [editModal, reviewsSlots, disabled]);

  const handleReviewSelect = React.useCallback((review: AgentReview) => {
    if (!modal.data) return;

    const isAlreadySelected = modal.data.review?.id === review.id;

    modal.setData({
      slotIndex: modal.data.slotIndex,
      review: isAlreadySelected ? undefined : review,
    });
  }, [modal]);

  const confirmSelection = React.useCallback(() => {
    if (typeof modal.data?.slotIndex === 'undefined' || !modal.data.review) return;

    assignSlot(modal.data.slotIndex, modal.data.review);

    modal.close();
  }, [assignSlot, modal]);

  const handleUpdateReview = React.useCallback((formValues: TemplateFieldsFormValues) => {
    setReviewSlots((prev) => {
      const next = new Map(prev);

      next.set(editModal.data!.slotIndex, {
        ...next.get(editModal.data!.slotIndex)!,
        author: formValues['agent_data_reviews($POSITION)_author'],
        title: formValues['agent_data_reviews($POSITION)_title'],
        text: formValues['agent_data_reviews($POSITION)_text'],
        date: formValues['agent_data_reviews($POSITION)_date'] ? new Date(formValues['agent_data_reviews($POSITION)_date']).toISOString() : '',
      });

      return next;
    });

    editModal.close();
  }, [editModal]);

  const slotKeys = React.useMemo(
    () => Array.from({ length: totalSlots }, () => uniqueId('agent-reviews-slot-')),
    [totalSlots],
  );

  const filteredAgentReviewFields = React.useCallback(
    (slotIndex: number) => AGENT_DATA_PREVIEWS_TEMPLATE_FIELD_CONFIG.filter(({ name }) => decodedDataset.variables.some((v) => v.raw === name.replace('$POSITION', slotIndex.toString()))),
    [decodedDataset.variables],
  );

  if (totalSlots === 0) return null;

  return (
    <>
      <Card asContent={asContent}>
        <Card.Header title={`${pluralizeWord('Recension', 'e', 'i', totalSlots)} agente${totalSlots > 1 ? ` (${reviewsSlots.size} di ${totalSlots})` : ''}`} />

        {preSelectedReviewsError && (
          <Card.Content>
            <Message
              type="critical"
              message="Non è stato possibile caricare le recensioni dell'agente."
            />
          </Card.Content>
        )}

        {!arePreSelectedReviewsLoading && !preSelectedReviewsError && preSelectedReviews.length === 0 && (
          <Card.Content>
            <Message
              type="warning"
              message="L'agente non ha nessuna recensione."
            />
          </Card.Content>
        )}

        {arePreSelectedReviewsLoading && Array.from({ length: totalSlots }).map((_, slotIndex) => (
          <React.Fragment key={slotKeys[slotIndex]}>
            <Card.Divider />
            <Card.Content aria-label="Recensione in caricamento">
              <CardSkeleton
                key={slotKeys[slotIndex]}
                paddingSize="XS"
                asContent="unpadded"
              >
                <TextSkeleton count={2} />
              </CardSkeleton>
            </Card.Content>
          </React.Fragment>
        ))}

        {preSelectedReviews.length > 0 && Array.from({ length: totalSlots }).map((_, slotIndex) => {
          const assignedReview = reviewsSlots.get(slotIndex);

          return (
            <React.Fragment key={slotKeys[slotIndex]}>
              <Card.Divider />
              <Card.Content
                highlighted={!assignedReview}
                aria-label="Recensione dell'agente"
              >
                <HStack
                  gap={150}
                  wrap="nowrap"
                  alignItems="flex-start"
                >
                  {totalSlots > 1 && (
                    <div css={styles.index}>
                      {slotIndex + 1}
                    </div>
                  )}

                  {!assignedReview && (
                    <div css={styles.emptySlot}>
                      <HStack wrap="nowrap" justifyContent="space-between">
                        <HStack gap={100}>
                          <ReviewAvatar size="S" />
                          <Text.BodySmall fontWeight={FontWeight.MEDIUM}>
                            Non selezionata
                          </Text.BodySmall>
                        </HStack>

                        <ActionIcon
                          size="S"
                          label="Seleziona"
                          aria-label="Azione per selezionare una recensione"
                          icon={{ path: ICON_PLUS }}
                          disabled={disabled}
                          emphasis="high"
                          onClick={openModal(slotIndex)}
                        />
                      </HStack>
                    </div>
                  )}

                  {assignedReview && (
                    <AgentReviewSelectablePreview
                      review={assignedReview}
                      asContent="unpadded"
                      primaryActions={[
                        <ActionIcon
                          size="S"
                          label="Cambia"
                          aria-label="Azione per cambiare la recensione selezionata"
                          icon={{ path: ICON_SYNC }}
                          disabled={disabled}
                          onClick={openModal(slotIndex)}
                        />,
                      ]}
                      secondaryActions={[
                        <Dropdown.Option
                          label="Modifica"
                          aria-label="Azione per modificare la recensione selezionata"
                          disabled={disabled}
                          onClick={openEditModal(slotIndex)}
                        />,
                        <Dropdown.Option
                          label="Deseleziona"
                          aria-label="Azione per deselezionare la recensione"
                          disabled={disabled}
                          onClick={() => deselectSlot(slotIndex)}
                        />,
                      ]}
                    />
                  )}
                </HStack>
              </Card.Content>
            </React.Fragment>
          );
        })}
      </Card>

      <Portal>
        {modal.data && (
          <SimpleModal
            {...modal}
            size={ModalSize.MEDIUM}
            title={`Seleziona recensione agente${totalSlots > 1 ? ` #${modal.data.slotIndex + 1}` : ''}`}
            aria-label={`Seleziona recensione agente per slot #${modal.data.slotIndex + 1}`}
            padded={false}
            footer={(
              <Action
                color="primary"
                label="Conferma"
                emphasis="high"
                disabled={!modal.data?.review}
                onClick={confirmSelection}
              />
            )}
          >
            <div css={styles.searchBar}>
              <Text.Body>
                Seleziona la recensione {totalSlots > 1 ? `#${modal.data.slotIndex + 1}` : ''} dalla lista di recensioni a 5 stelle, cliccando sulla relativa anteprima.
              </Text.Body>

              <SearchByQueryAction
                initialQuery={searchQuery}
                onSearch={setSearchQuery}
                width="100%"
                placeholder="Cerca per autore, titolo o testo..."
                size="M"
                variant="input"
              />
            </div>

            <ScrollableArea height={isMobile ? '40rem' : '25rem'}>
              <Spacing padding={300}>
                {allReviewsError && (
                  <Message
                    type="critical"
                    message="Non è stato possibile caricare le recensioni dell'agente."
                  />
                )}

                {!areAllReviewsLoading && !allReviewsError && allReviews.length === 0 && (
                  <Message
                    type="warning"
                    message="Nessuna recensione trovata."
                  />
                )}

                {areAllReviewsLoading && (
                  <Stack gap={200} aria-label="Recensioni in caricamento">
                    {Array.from({ length: 2 }).map((_, slotIndex) => (
                      <CardSkeleton
                        key={slotKeys[slotIndex]}
                        paddingSize="XS"
                      />
                    ))}
                  </Stack>
                )}

                {allReviews.length > 0 && (
                  <Stack gap={200} aria-label="Recensioni dell'agente">
                    {allReviews.map((review) => {
                      const isSelectedInThisModal = modal.data?.review?.id === review.id;
                      const isSelectedInAnotherSlot = selectedReviewIds.has(review.id!) && reviewsSlots.get(modal.data?.slotIndex ?? -1)?.id !== review.id;
                      const isClickable = !isSelectedInAnotherSlot;
                      const isSelected = isSelectedInThisModal || isSelectedInAnotherSlot;

                      let selectedIndex: number | undefined;

                      if (isSelectedInThisModal) {
                        selectedIndex = (modal.data?.slotIndex ?? 0) + 1;
                      } else if (isSelectedInAnotherSlot) {
                        selectedIndex = (slotIndexByReviewId.get(review.id!) ?? 0) + 1;
                      }

                      return (
                        <AgentReviewSelectablePreview
                          key={review.id}
                          review={review}
                          selected={isSelected}
                          disabled={isSelectedInAnotherSlot}
                          operation={isSelected ? 'remove' : 'add'}
                          onClick={isClickable ? () => handleReviewSelect(review) : undefined}
                          index={totalSlots > 1 ? selectedIndex : undefined}
                        />
                      );
                    })}
                  </Stack>
                )}
              </Spacing>
            </ScrollableArea>
          </SimpleModal>
        )}
      </Portal>
      <Portal>
        {editModal.data && (
          <SimpleModal
            {...editModal}
            size={ModalSize.MEDIUM}
            title={`Modifica recensione agente${totalSlots > 1 ? ` #${editModal.data.slotIndex + 1}` : ''}`}
            aria-label={`Modifica recensione agente per slot #${editModal.data.slotIndex + 1}`}
            footer={(
              <Action
                color="primary"
                label="Modifica"
                emphasis="high"
                disabled={!editModal.data?.review}
                onClick={() => editReviewFormRef.current?.handleSubmit()}
              />
            )}
          >
            <TemplateFieldsForm
              innerRef={editReviewFormRef}
              fields={filteredAgentReviewFields(editModal.data.slotIndex + 1)}
              initialValues={{
                'agent_data_reviews($POSITION)_author': editModal.data.review?.author || '',
                'agent_data_reviews($POSITION)_title': editModal.data.review?.title || '',
                'agent_data_reviews($POSITION)_text': editModal.data.review?.text || '',
                'agent_data_reviews($POSITION)_date': editModal.data.review?.date ? formatInputDate(new Date(editModal.data.review.date)) : '',
              }}
              disabled={disabled}
              onSubmit={handleUpdateReview}
            />
          </SimpleModal>
        )}
      </Portal>
    </>
  );
};

export default AgentReviewsSelector;
